// src/context/AuthContext.jsx
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { CONFIG } from "../lib/config.js";
import { auth } from "../lib/firebase.js";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {

    const [token, setToken] = useState(() => {
        const t = localStorage.getItem('session_token');
        return t;
    });

    const [tenantId, setTenantId] = useState(() => {
        const tId = localStorage.getItem('tenant_id');
        return tId;
    });

    const [userEmail, setUserEmail] = useState(null);

    const [user, setUser] = useState(() => {
        const savedUser = localStorage.getItem('user_data');
        return savedUser ? JSON.parse(savedUser) : null;
    });

    const [tenantStatus, setTenantStatus] = useState(() => localStorage.getItem('tenant_status'));
    const [trialEndDate, setTrialEndDate] = useState(() => localStorage.getItem('trial_end_date'));
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [authLoading, setAuthLoading] = useState(true);
    const [isProvisioning, setIsProvisioning] = useState(() => localStorage.getItem('is_provisioning') === 'true');

    const [activeTour, setActiveTour] = useState(null);
    const startTour = useCallback((tourName = 'intro_tour') => setActiveTour(tourName), []);
    const endTour = useCallback(() => setActiveTour(null), []);

    useEffect(() => {
        if (!auth) {
            setAuthLoading(false);
            return;
        }

        const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
            if (firebaseUser) {
                try {
                    const freshToken = await firebaseUser.getIdToken();
                    setToken(freshToken);
                    localStorage.setItem('session_token', freshToken);
                    setUserEmail(firebaseUser.email);
                    setIsAuthenticated(true);
                } catch (err) {
                    console.error("🔵 [AuthContext] -> Failed to retrieve Firebase token:", err);
                    setIsAuthenticated(false);
                    setToken(null);
                    localStorage.removeItem('session_token');
                }
            } else {
                const storedToken = localStorage.getItem('session_token');
                if (storedToken) {
                    setToken(storedToken);
                    setIsAuthenticated(true);
                    const savedUser = JSON.parse(localStorage.getItem('user_data') || '{}');
                    setUserEmail(savedUser.email || null);
                } else {
                    setToken(null);
                    setUserEmail(null);
                    setIsAuthenticated(false);
                }
            }
            setAuthLoading(false);
        });

        return () => unsubscribe();
    }, []);

    const login = useCallback((newToken, newTenantId, email, userData, status, trialEnd) => {
        setToken(newToken);
        setTenantId(newTenantId);
        setUserEmail(email);
        setUser(userData);
        setTenantStatus(status);
        setTrialEndDate(trialEnd);
        setIsAuthenticated(true);

        if (newToken) localStorage.setItem('session_token', newToken);
        if (newTenantId !== null && newTenantId !== undefined) localStorage.setItem('tenant_id', newTenantId);
        if (userData) localStorage.setItem('user_data', JSON.stringify(userData));
        if (status) localStorage.setItem('tenant_status', status);
        if (trialEnd) localStorage.setItem('trial_end_date', trialEnd);
    }, []);

    const logout = useCallback(async () => {
        setToken(null);
        setTenantId(null);
        setUserEmail(null);
        setUser(null);
        setTenantStatus(null);
        setTrialEndDate(null);
        setIsAuthenticated(false);
        setIsProvisioning(false);

        localStorage.removeItem('session_token');
        localStorage.removeItem('tenant_id');
        localStorage.removeItem('user_data');
        localStorage.removeItem('tenant_status');
        localStorage.removeItem('trial_end_date');
        localStorage.removeItem('is_provisioning');
        localStorage.removeItem('theme_preference');

        if (auth) {
            try { await signOut(auth); } catch (err) { console.error("🔵 [AuthContext] Firebase signout failed:", err); }
        }
    }, []);

    const updateUser = useCallback((newUserData) => {
        setUser(prev => {
            const updated = { ...prev, ...newUserData };
            localStorage.setItem('user_data', JSON.stringify(updated));
            return updated;
        });
    }, []);

    const startProvisioning = useCallback((newToken, newTenantId, email, userData, status, trialEnd) => {
        login(newToken, newTenantId, email, userData, status, trialEnd);
        setIsProvisioning(true);
        localStorage.setItem('is_provisioning', 'true');
    }, [login]);

    const completeProvisioning = useCallback(() => {
        setIsProvisioning(false);
        localStorage.removeItem('is_provisioning');
    }, []);

    const getAuthHeaders = useCallback(() => {
        const activeToken = token || localStorage.getItem('session_token');
        if (!activeToken || tenantId === null || tenantId === undefined) {
            return {};
        }
        const headers = { 'Authorization': `Bearer ${activeToken}`, [CONFIG.TENANT_ID_HEADER]: tenantId };
        if (userEmail) headers['X-User-Email'] = userEmail;
        return headers;
    }, [token, tenantId, userEmail]);

    const fetchWithAuth = useCallback(async (url, options = {}) => {
        let currentToken = token || localStorage.getItem('session_token');

        if (auth && auth.currentUser) {
            try {
                currentToken = await auth.currentUser.getIdToken();
                if (currentToken !== token) {
                    setToken(currentToken);
                    localStorage.setItem('session_token', currentToken);
                }
            } catch (err) {
                console.error("🔵 [AuthContext] Failed to refresh token prior to request:", err);
            }
        }

        const baseHeaders = { 'Authorization': `Bearer ${currentToken}` };
        if (tenantId !== null && tenantId !== undefined) {
            baseHeaders[CONFIG.TENANT_ID_HEADER] = tenantId;
        } else {
            console.warn("🔵 [AuthContext] fetchWithAuth Warning: tenantId is null/undefined!");
        }
        if (userEmail) baseHeaders['X-User-Email'] = userEmail;

        let response = await fetch(url, { ...options, headers: { ...baseHeaders, ...(options.headers || {}) } });

        if (response.status === 401 && auth && auth.currentUser) {
            console.warn("🔵 [AuthContext] 401 response received. Forcing token refresh retry...");
            try {
                currentToken = await auth.currentUser.getIdToken(true);
                setToken(currentToken);
                localStorage.setItem('session_token', currentToken);
                baseHeaders['Authorization'] = `Bearer ${currentToken}`;
                response = await fetch(url, { ...options, headers: { ...baseHeaders, ...(options.headers || {}) } });
            } catch (retryErr) { console.error("🔵 [AuthContext] Forced token refresh failed.", retryErr); }
        }

        if (response.status === 401) await logout();
        return response;
    }, [token, tenantId, userEmail, logout]);

    const checkProvisioningStatus = useCallback(async () => {
        const activeToken = token || localStorage.getItem('session_token');
        if (!activeToken || tenantId === null || tenantId === undefined) return null;
        try {
            const response = await fetchWithAuth(`${CONFIG.API_BASE_URL}/tenant/status`);
            if (!response.ok) return null;
            const data = await response.json();
            if (data.status && data.status !== tenantStatus) {
                setTenantStatus(data.status);
                localStorage.setItem('tenant_status', data.status);
            }
            if (data.trial_end_date) {
                setTrialEndDate(data.trial_end_date);
                localStorage.setItem('trial_end_date', data.trial_end_date);
            }
            return data.status;
        } catch (error) {
            console.error("Provisioning check failed:", error);
            return null;
        }
    }, [token, tenantId, fetchWithAuth, tenantStatus]);

    const hasSectionAccess = useCallback((sectionName) => {
        if (!user) return false;
        if (user.role === 'admin') return true;
        return Array.isArray(user.sections) && user.sections.includes(sectionName);
    }, [user]);

    const markTourComplete = useCallback(async (tourName) => {
        if (!user || !userEmail) return;
        const updatedOnboarding = { ...(user.onboarding_state || {}), [tourName]: true };
        updateUser({ onboarding_state: updatedOnboarding });
        try {
            const response = await fetchWithAuth(`${CONFIG.API_BASE_URL}/tenant/user/onboarding`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ tour_name: tourName, completed: true })
            });
            if (!response.ok) {
                const errorText = await response.text();
                console.error(`Backend refused to save ${tourName}. Status: ${response.status}`, errorText);
            }
        } catch (err) { console.error(`Network failed to persist ${tourName} completion:`, err); }
    }, [user, userEmail, updateUser, fetchWithAuth]);

    const contextValue = useMemo(() => ({
        token, tenantId, userEmail, user, tenantStatus, trialEndDate, isAuthenticated, authLoading, isProvisioning,
        login, logout, updateUser, startProvisioning, completeProvisioning, checkProvisioningStatus, getAuthHeaders,
        fetchWithAuth, hasSectionAccess, markTourComplete, activeTour, startTour, endTour
    }), [
        token, tenantId, userEmail, user, tenantStatus, trialEndDate, isAuthenticated, authLoading, isProvisioning,
        login, logout, updateUser, startProvisioning, completeProvisioning, checkProvisioningStatus, getAuthHeaders,
        fetchWithAuth, hasSectionAccess, markTourComplete, activeTour, startTour, endTour
    ]);

    return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) throw new Error("useAuth must be used within an AuthProvider");
    return context;
};